'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './QuantitySelector.css';

export interface QuantitySelectorProps {
  /** Current quantity value */
  value: number;
  /** Called when the value changes */
  onChange: (value: number) => void;
  /** Minimum allowed value */
  min?: number;
  /** Maximum allowed value */
  max?: number;
  /** Disabled state */
  disabled?: boolean;
  /** Accessible label for the control */
  'aria-label'?: string;
}

export const QuantitySelector: React.FC<QuantitySelectorProps> = ({
  value,
  onChange,
  min = 1,
  max,
  disabled = false,
  'aria-label': ariaLabel = 'Quantity',
}) => {
  const [displayValue, setDisplayValue] = useState(Number(value));
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync internal display state when the controlled `value` prop changes
    setDisplayValue(Number(value));
  }, [value]);

  const handleChange = useCallback(
    (newValue: number) => {
      setDisplayValue(newValue);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        onChange(newValue);
      }, 300);
    },
    [onChange]
  );

  const canDecrement = !disabled && displayValue > min;
  const canIncrement = !disabled && (max === undefined || displayValue < max);

  const handleDecrement = () => {
    handleChange(displayValue - 1);
  };

  const handleIncrement = () => {
    handleChange(displayValue + 1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === '-') {
      e.preventDefault();
      if (canDecrement) handleChange(displayValue - 1);
    } else if (e.key === 'ArrowUp' || e.key === '+') {
      e.preventDefault();
      if (canIncrement) handleChange(displayValue + 1);
    }
  };

  return (
    <div
      className={`hi-ui-quantity-selector${disabled ? ' hi-ui-quantity-selector--disabled' : ''}`}
      role="group"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        className={`hi-ui-quantity-selector__btn hi-ui-quantity-selector__btn--minus${!canDecrement ? ' hi-ui-quantity-selector__btn--disabled' : ''}`}
        onClick={handleDecrement}
        disabled={!canDecrement}
        aria-label="Decrease quantity"
        tabIndex={canDecrement ? 0 : -1}
      />
      <span className="hi-ui-quantity-selector__value" aria-live="polite">
        {displayValue}
      </span>
      <button
        type="button"
        className={`hi-ui-quantity-selector__btn hi-ui-quantity-selector__btn--plus${!canIncrement ? ' hi-ui-quantity-selector__btn--disabled' : ''}`}
        onClick={handleIncrement}
        disabled={!canIncrement}
        aria-label="Increase quantity"
        tabIndex={canIncrement ? 0 : -1}
      />
    </div>
  );
};
